import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import useAuthStatus from "../../hooks/useAuthStatus";   // ← Added
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { getTransactionHistory } from "../../api/apiServices";

export default function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();   // ← Added

  const cardRef = useRef(null);

  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/auth");
      return;
    }

    const fetchTx = async () => {
      try {
        const res = await getTransactionHistory();
        const tx = res.data?.find(t => t._id === id || t.id === id);
        setTransaction(tx);
      } catch (error) {
        console.error("Failed to fetch transaction:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTx();
  }, [id, isAuthenticated, navigate]);

  

  // Status configuration
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return {
          color: "#059669",
          bg: "#ECFDF5",
          icon: "ri-check-line",
          text: "Completed"
        };
      case "pending":
        return {
          color: "#D97706",
          bg: "#FFFBEB",
          icon: "ri-time-line",
          text: "Pending"
        };
      case "processing":
        return {
          color: "#D97706",
          bg: "#EFF6FF",
          icon: "ri-loader-2-line",
          text: "Processing"
        };
      case "failed":
        return {
          color: "#DC2626",
          bg: "#FEF2F2",
          icon: "ri-close-circle-line",
          text: "Failed"
        };
      default:
        return {
          color: "#64748B",
          bg: "#F1F5F9",
          icon: "ri-question-line",
          text: "Unknown"
        };
    }
  };

  const statusConfig = transaction ? getStatusConfig(transaction.status) : null;

  const downloadAsImage = async () => {
    if (!cardRef.current || !isAuthenticated) return;

    try {
      const canvas = await html2canvas(cardRef.current, { 
        scale: 2,
        backgroundColor: "#ffffff"
      });
      const link = document.createElement("a");
      link.download = `Nivora_Transaction_${id}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("Failed to download receipt:", error);
    }
  };

  const shareReceipt = async () => {
    if (!isAuthenticated || !transaction) return;

    try {
      // Try native Web Share API first
      if (navigator.share) {
        await navigator.share({
          title: `Nivora Transaction Receipt #${id}`,
          text: `₦${parseFloat(transaction.nairaAmount).toLocaleString()} - ${transaction.type}`,
          url: window.location.href,
        });
      } else {
        // Fallback: Copy receipt details to clipboard
        const receiptText = `
Nivora Transaction Receipt
────────────────────
Amount: ₦${parseFloat(transaction.nairaAmount).toLocaleString()}
Type: ${transaction.type}
Status: ${transaction.status}
Date: ${new Date(transaction.createdAt).toLocaleString()}
Reference: ${transaction.paystackReference || transaction._id}
        `.trim();

        await navigator.clipboard.writeText(receiptText);
        alert("Receipt details copied to clipboard!");
      }
    } catch (error) {
      console.error("Share failed:", error);
      alert("Could not share receipt. Try downloading as image instead.");
    }
  };

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-4xl text-emerald-600 mb-3"></i>
          <p className="text-slate-500">Loading receipt...</p>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-red-400 mb-4"></i>
          <p className="text-slate-600 font-medium">Transaction not found</p>
          <button
            onClick={() => navigate("/transactions")}
            className="mt-6 px-6 py-3 bg-slate-800 text-white rounded-2xl text-sm font-semibold"
          >
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28 px-5">
      {/* Header */}
      <div className="pt-12 flex justify-between items-center mb-6">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-1 text-slate-600 hover:text-slate-800 transition-colors"
        >
          ← Back
        </button>
        <h1 className="font-bold text-lg">Transaction Receipt</h1>
        <div className="w-8" /> {/* Spacer for alignment */}
      </div>

      {/* Receipt Card */}
      <div 
        ref={cardRef} 
        className="bg-white rounded-3xl shadow-2xl p-8 mb-8 overflow-hidden"
      >
        {/* Status Badge */}
        <div className="flex justify-end mb-6">
          <div 
            className="flex items-center gap-2 px-4 py-1.5 rounded-2xl text-sm font-semibold"
            style={{ 
              backgroundColor: statusConfig.bg, 
              color: statusConfig.color 
            }}
          >
            <i className={`${statusConfig.icon} text-lg`}></i>
            <span>{statusConfig.text}</span>
          </div>
        </div>

        <div className="text-center mb-8">
          <div
          className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-4"
          style={{ backgroundColor: statusConfig.bg }}
          >
            <i
            className={`${statusConfig.icon} text-6xl`}
            style={{ color: statusConfig.color }}
            />
          </div>
          <p className="text-emerald-600 font-semibold tracking-widest text-sm">TRANSACTION RECEIPT</p>
          <p className="text-4xl font-bold mt-3 text-slate-800">
            ₦{parseFloat(transaction.nairaAmount).toLocaleString()}
          </p>
        </div>

        <div className="space-y-5 py-6 border-t border-b border-dashed border-slate-200">
          <div className="flex justify-between">
            <span className="text-slate-500">Type</span>
            <span className="font-semibold capitalize">{transaction.type}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Asset</span>
            <span className="font-semibold">
              {transaction.cryptoAmount} {transaction.asset}
            </span>
          </div>
          {transaction.rate && (
            <div className="flex justify-between">
              <span className="text-slate-500">Rate</span>
              <span>₦{transaction.rate?.toLocaleString()}</span>
            </div>
          )}
          {transaction.fee && (
            <div className="flex justify-between">
              <span className="text-slate-500">Fee</span>
              <span className="text-red-600">-₦{transaction.fee?.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between pt-4 border-t font-bold">
            <span>Net Amount</span>
            <span className="text-emerald-600">
              ₦{parseFloat(transaction.nairaAmount).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="pt-6 space-y-4 text-sm">
          {transaction.bankName && (
            <div className="flex justify-between">
              <span className="text-slate-500">Recipient</span>
              <span className="text-right">
                {transaction.accountName}<br />
                {transaction.bankName}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-slate-500">Reference</span>
            <span className="font-mono text-slate-700">
              {transaction.paystackReference || transaction._id}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Date</span>
            <span>{new Date(transaction.createdAt).toLocaleString("en-NG")}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Status</span>
            <span className="font-medium" style={{ color: statusConfig.color }}>
              {statusConfig.text}
            </span>
          </div>
        </div>

        <div className="text-center mt-10 text-xs text-slate-400">
          Secured by Nivora • Transaction ID: {transaction._id}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 max-w-xs mx-auto">
        <button
          onClick={downloadAsImage}
          className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl font-semibold flex items-center justify-center gap-2 active:scale-[0.985] transition-transform"
        >
          <i className="ri-image-line"></i> Download as Image
        </button>

        <button
          onClick={shareReceipt}
          className="w-full py-4 bg-white border border-slate-300 rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors"
        >
          <i className="ri-share-line"></i> Share Receipt
        </button>

        <button
          onClick={() => navigate("/transactions")}
          className="w-full py-4 bg-white border border-slate-300 rounded-2xl font-semibold"
        >
          Back to History
        </button>
      </div>

      <MobileBottomNav />
    </div>
  );
}